import { Suspense, lazy } from 'react';
import type { ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate, Outlet } from 'react-router-dom';

import Layout from '@/shared/layout/Layout';
import NotFound from '@/shared/pages/NotFound';
import ProtectedRoute from '@/components/ProtectedRoute';
import Providers from '@/app/Providers';
import { RescanControllerProvider } from '@/features/rescan/RescanController';
import { APP_ROUTES } from '@/app/routePaths';
import { dbConstants } from '@/shared/adapters/constants';
import RouteSkeleton from '@/shared/components/RouteSkeleton';
import type { RouteSkeletonVariant } from '@/shared/components/RouteSkeleton';
import SupportAssistant from '@/shared/layout/../assistant/SupportAssistant';

const DashboardPage = lazy(() => import('@/features/dashboard/pages/Dashboard'));
const CompanyKnowledgeBasePage = lazy(() => import('@/features/kb/pages/CompanyKnowledgeBase'));
const KbSourcePage = lazy(() => import('@/features/kb/pages/KbSourcePage'));
const CompetitorsPage = lazy(() => import('@/features/competitors/pages/Competitors'));
const WritingStylesPage = lazy(() => import('@/features/writing-style/pages/WritingStyles'));
const WritingStylePage = lazy(
  () => import('@/features/writing-style/pages/WritingStyle'),
);
const PersonasPage = lazy(() => import('@/features/persona/pages/Personas'));
const CompanyBrandVoicePage = lazy(() => import('@/features/brand-voice/pages/CompanyBrandVoice'));
const CompanyPage = lazy(() => import('@/features/company/pages/Company'));
const MediaLibraryPage = lazy(() => import('@/features/media/pages/MediaLibrary'));
const ProductsPage = lazy(() => import('@/features/product/pages/Products'));
const ProductPage = lazy(() => import('@/features/product/pages/Product'));
const CampaignsPage = lazy(() => import('@/features/campaign/pages/Campaigns'));
const CampaignPage = lazy(() => import('@/features/campaign/pages/CampaignPageWrapper'));
const ContentsPage = lazy(() => import('@/features/content/pages/Contents'));
const ContentFormPage = lazy(() => import('@/features/content/pages/ContentForm'));
const NewContentPage = lazy(() => import('@/features/content/pages/NewContent'));
const SuggestionsPage = lazy(() => import('@/features/suggestions/pages/Suggestions'));
const ChannelsPage = lazy(() => import('@/features/channel/pages/Channels'));
const EditorialCalendarPage = lazy(() => import('@/features/content/pages/EditorialCalendarPage'));
const TrustSignalDetailPage = lazy(() => import('@/features/trust-signal/pages/TrustSignalDetail'));
const CompanyTrustSignalPage = lazy(() => import('@/features/trust-signal/pages/CompanyTrustSignal'));
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'));
const UserPage = lazy(() => import('@/features/settings/pages/User'));
const RolePage = lazy(() => import('@/features/settings/pages/Role'));
const ProfilePage = lazy(() => import('@/features/profile/pages/Profile'));
const AnalysisPage = lazy(() => import('@/features/onboarding/pages/AnalysisPage'));
const OnboardingPage = lazy(() => import('@/features/onboarding/pages/OnboardingPage'));
const PortalCompletePage = lazy(() => import('@/shared/pages/PortalComplete'));
const SiteHealthPage = lazy(() => import('@/features/site-health/pages/SiteHealth'));
const VisibilityReport = lazy(() => import('@/features/visibility-report/pages/VisibilityReport'));

const withRouteSuspense = (element: ReactNode, fallbackVariant: RouteSkeletonVariant = 'list') => (
  <Suspense fallback={<RouteSkeleton variant={fallbackVariant} />}>
    {element}
  </Suspense>
);

const settingsElement = (
  <ProtectedRoute permissions={[dbConstants.PERMISSION_FIRM_UPDATE]}>
    {withRouteSuspense(<SettingsPage />, 'detail')}
  </ProtectedRoute>
);

const profileElement = (
  <ProtectedRoute permissions={[dbConstants.PERMISSION_USER_PROFILE_READ]}>
    {withRouteSuspense(<ProfilePage />, 'form')}
  </ProtectedRoute>
);

export const appRoutes: RouteObject[] = [
  {
    /* Root wrapper: Providers + SupportAssistant live here so they
       persist across all child route groups without remounting. */
    element: (
      <Providers>
        <RescanControllerProvider>
          <Outlet />
          <SupportAssistant />
        </RescanControllerProvider>
      </Providers>
    ),
    children: [
      /* ── Group 1: Layout-based pages ────────────────────────────── */
      {
        element: <Layout />,
        children: [
          {
            path: APP_ROUTES.DASHBOARD,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_DASHBOARD_READ]}>
                {withRouteSuspense(<DashboardPage />, 'list')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.SUGGESTIONS,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_DASHBOARD_READ]}>
                {withRouteSuspense(<SuggestionsPage />, 'list')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.COMPANY_KNOWLEDGE_BASE,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_KNOWLEDGE_BASE_READ]}>
                {withRouteSuspense(<CompanyKnowledgeBasePage />, 'detail')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.KB_SOURCE_PATH,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_KNOWLEDGE_BASE_READ]}>
                {withRouteSuspense(<KbSourcePage />, 'detail')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.COMPETITORS,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_KNOWLEDGE_BASE_READ]}>
                {withRouteSuspense(<CompetitorsPage />, 'list')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.WRITING_STYLES,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_WRITING_STYLE_READ]}>
                {withRouteSuspense(<WritingStylesPage />, 'list')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.WRITING_STYLE_NEW,
            element: (
              <ProtectedRoute key="writing-style-new" permissions={[dbConstants.PERMISSION_WRITING_STYLE_CREATE]}>
                {withRouteSuspense(<WritingStylePage />, 'form')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.WRITING_STYLE_EDIT_PATH,
            element: (
              <ProtectedRoute key="writing-style-edit" permissions={[dbConstants.PERMISSION_WRITING_STYLE_UPDATE]}>
                {withRouteSuspense(<WritingStylePage />, 'form')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.PERSONAS,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_PERSONA_READ]}>
                {withRouteSuspense(<PersonasPage />, 'list')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.COMPANY_BRAND_VOICE,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_BRAND_VOICE_READ]}>
                {withRouteSuspense(<CompanyBrandVoicePage />, 'detail')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.COMPANIES,
            element: <Navigate to={APP_ROUTES.DASHBOARD} replace />,
          },
          {
            path: APP_ROUTES.COMPANY_NEW,
            element: (
              <ProtectedRoute key="company-new" permissions={[dbConstants.PERMISSION_COMPANY_CREATE]}>
                <Navigate to={APP_ROUTES.ONBOARDING} state={{ addingCompany: true }} replace />
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.COMPANY_EDIT_PATH,
            element: (
              <ProtectedRoute key="company-edit" permissions={[dbConstants.PERMISSION_COMPANY_UPDATE]}>
                {withRouteSuspense(<CompanyPage />, 'form')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.PRODUCTS,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_PRODUCT_READ]}>
                {withRouteSuspense(<ProductsPage />, 'list')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.PRODUCT_NEW,
            element: (
              <ProtectedRoute key="product-new" permissions={[dbConstants.PERMISSION_PRODUCT_CREATE]}>
                {withRouteSuspense(<ProductPage />, 'form')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.PRODUCT_EDIT_PATH,
            element: (
              <ProtectedRoute key="product-edit" permissions={[dbConstants.PERMISSION_PRODUCT_UPDATE]}>
                {withRouteSuspense(<ProductPage />, 'form')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.CAMPAIGNS,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_CAMPAIGN_READ]}>
                {withRouteSuspense(<CampaignsPage />, 'list')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.CAMPAIGN_NEW,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_CAMPAIGN_CREATE]}>
                {withRouteSuspense(<CampaignPage />, 'form')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.CAMPAIGN_EDIT_PATH,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_CAMPAIGN_UPDATE]}>
                {withRouteSuspense(<CampaignPage />, 'form')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.SOURCE_CONTENTS,
            element: <NotFound />,
          },
          {
            path: APP_ROUTES.CONTENTS,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_CONTENT_READ]}>
                {withRouteSuspense(<ContentsPage />, 'list')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.CONTENT_NEW,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_CONTENT_CREATE]}>
                {withRouteSuspense(<NewContentPage />, 'form')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.CHANNELS,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_PUBLISH_READ]}>
                {withRouteSuspense(<ChannelsPage />, 'list')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.EDITORIAL_CALENDAR,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_PUBLISH_READ]}>
                {withRouteSuspense(<EditorialCalendarPage />, 'list')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.MEDIA,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_MEDIA_READ]}>
                {withRouteSuspense(<MediaLibraryPage />, 'list')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.TRUST_SIGNAL_DETAIL_PATH,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_ASSET_READ]}>
                {withRouteSuspense(<TrustSignalDetailPage />, 'detail')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.COMPANY_TRUST_SIGNAL,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_ASSET_READ]}>
                {withRouteSuspense(<CompanyTrustSignalPage />, 'detail')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.SETTINGS,
            element: settingsElement,
          },
          {
            path: APP_ROUTES.SETTINGS_TAB_PATH,
            element: settingsElement,
          },
          {
            path: APP_ROUTES.USER_NEW,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_USER_CREATE]}>
                {withRouteSuspense(<UserPage />, 'form')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.USER_EDIT_PATH,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_USER_UPDATE]}>
                {withRouteSuspense(<UserPage />, 'form')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.ROLE_NEW,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_ROLE_CREATE]}>
                {withRouteSuspense(<RolePage />, 'form')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.ROLE_EDIT_PATH,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_ROLE_UPDATE]}>
                {withRouteSuspense(<RolePage />, 'form')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.SUBSCRIPTION,
            element: <Navigate to={APP_ROUTES.settingsTab('subscription')} replace />,
          },
          {
            path: APP_ROUTES.PROFILE,
            element: profileElement,
          },
          {
            path: APP_ROUTES.PROFILE_TAB_PATH,
            element: profileElement,
          },
          {
            path: APP_ROUTES.ANALYSIS,
            element: withRouteSuspense(<AnalysisPage />, 'detail'),
          },
          {
            path: APP_ROUTES.SITE_HEALTH,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_DASHBOARD_READ]}>
                {withRouteSuspense(<SiteHealthPage />, 'detail')}
              </ProtectedRoute>
            ),
          },
          {
            path: APP_ROUTES.RESCAN,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_DASHBOARD_READ]}>
                {withRouteSuspense(<VisibilityReport />, 'detail')}
              </ProtectedRoute>
            ),
          },
          {
            path: '*',
            element: <NotFound />,
          },
        ],
      },
      /* ── Group 2: Standalone full-screen pages (no sidebar) ─────── */
      {
        element: <Outlet />,
        children: [
          {
            path: APP_ROUTES.ONBOARDING,
            element: withRouteSuspense(<OnboardingPage />, 'onboarding'),
          },
          {
            path: APP_ROUTES.SOURCE_CONTENT_NEW,
            element: <NotFound />,
          },
          {
            path: APP_ROUTES.SOURCE_CONTENT_EDIT_PATH,
            element: <NotFound />,
          },
          {
            path: APP_ROUTES.CONTENT_EDIT_PATH,
            element: (
              <ProtectedRoute permissions={[dbConstants.PERMISSION_CONTENT_UPDATE]}>
                {withRouteSuspense(<ContentFormPage />, 'form')}
              </ProtectedRoute>
            ),
          },
          {
            path: '/portal-complete',
            element: withRouteSuspense(<PortalCompletePage />, 'detail'),
          },
        ],
      },
    ],
  },
];

export default appRoutes;